import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useExplorationActions,
  useExplorationState,
  useCurrentAccountVolumes,
} from '../context/ExplorationContext';
import type { ExplorationFilter } from '../types/exploration';
import { removeExplorationFilter, removeExplorationFilters } from '../lib/filters/removeExplorationFilter';
import ExplorationCanvasFilterButton from './ExplorationCanvasFilterButton';
import ExplorationFiltersSidePanel, {
  explorationFiltersPanelId,
} from './filters/ExplorationFiltersSidePanel';
import { getCapacityBounds } from './filters/utils/filterConverters';

export default function ExplorationCanvasFilters() {
  const state = useExplorationState();
  const actions = useExplorationActions();
  const volumes = useCurrentAccountVolumes();
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);
  const toggleRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef(false);
  const capacityBounds = useMemo(() => getCapacityBounds(volumes), [volumes]);

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      restoreFocusRef.current = true;
    }
    setFiltersPanelOpen(open);
  }, []);

  useEffect(() => {
    if (!filtersPanelOpen && restoreFocusRef.current) {
      restoreFocusRef.current = false;
      requestAnimationFrame(() => {
        const trigger = toggleRef.current?.querySelector<HTMLButtonElement>(
          `button[aria-controls="${explorationFiltersPanelId}"]`
        );
        trigger?.focus();
      });
    }
  }, [filtersPanelOpen]);

  const handleRemoveFilter = useCallback(
    (filter: ExplorationFilter) => {
      actions.setFilters(removeExplorationFilter(state.filters, filter));
    },
    [actions, state.filters]
  );

  const handleRemoveFilters = useCallback(
    (filters: ExplorationFilter[]) => {
      actions.setFilters(removeExplorationFilters(state.filters, filters));
    },
    [actions, state.filters]
  );

  return (
    <>
      {!filtersPanelOpen && (
        <div ref={toggleRef} data-exploration-filter-toggle className="flex min-w-0 max-w-full w-full justify-end">
          <ExplorationCanvasFilterButton
            filters={state.filters}
            capacityBounds={capacityBounds}
            onOpen={() => setFiltersPanelOpen(true)}
            onRemoveFilter={handleRemoveFilter}
            onRemoveFilters={handleRemoveFilters}
            aria-expanded={false}
            aria-controls={explorationFiltersPanelId}
          />
        </div>
      )}

      <ExplorationFiltersSidePanel
        open={filtersPanelOpen}
        onOpenChange={handleOpenChange}
        onResetFilters={() => actions.setFilters([])}
        onApplyFilters={actions.setFilters}
        appliedFilters={state.filters}
        capacityBounds={capacityBounds}
      />
    </>
  );
}
